import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("verifying"); // verifying | ok | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("This confirmation link is missing its token.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setState("ok");
        setMessage(data.message || "Email confirmed.");
      } catch (err) {
        setState("error");
        setMessage(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    })();
  }, [token]);

  return (
    <AuthShell title="Confirm your email" subtitle="Activating your Eligvia account.">
      <div className="flex flex-col items-center gap-4 py-4 text-center" data-testid="verify-email-status">
        {state === "verifying" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-slate-400" />
            <p className="text-sm text-slate-500">Confirming your email…</p>
          </>
        )}
        {state === "ok" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            <p className="text-sm font-medium text-slate-700" data-testid="verify-email-success">{message}</p>
            <Button asChild className="w-full bg-slate-900 hover:bg-slate-800" data-testid="verify-email-continue-btn">
              <Link to="/plans">Continue</Link>
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="h-12 w-12 text-red-500" />
            <p className="text-sm font-medium text-red-600" data-testid="verify-email-error">{message}</p>
            <Button asChild variant="outline" className="w-full" data-testid="verify-email-login-btn">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </>
        )}
      </div>
    </AuthShell>
  );
}
